import { ref } from "vue";
import type { OssArchiveRejectError } from "./oss-archive-error";
import {
	OSS_ARCHIVE_PARENT_MESSAGE_TYPE,
	isOssArchiveEmbeddedFrame,
	normalizeOssArchiveError
} from "./oss-archive-error";
import type { OssArchiveUiCopy } from "./oss-archive-dialog";
import { dispatchOssArchiveError, getOssArchiveConfigSnapshot, subscribeOssArchiveError } from "./oss-archive-integration";

export interface OssArchiveRestoreDialogPayload {
	error?: OssArchiveRejectError;
	pid?: number;
	orderId?: number;
	objectKeys?: string[];
	restoreStatus?: string;
	uiCopy?: OssArchiveUiCopy;
	onReady?: () => void;
}

export { isOssArchiveEmbeddedFrame };

export const ossArchiveRestoreDialogVisible = ref(false);
export const ossArchiveRestoreDialogPayload = ref<OssArchiveRestoreDialogPayload | null>(null);

function postRestoreToParent(error: unknown) {
	if (!isOssArchiveEmbeddedFrame()) {
		return;
	}
	window.parent.postMessage(
		{
			type: OSS_ARCHIVE_PARENT_MESSAGE_TYPE,
			error
		},
		"*"
	);
}

export function openOssArchiveRestoreDialog(payload: OssArchiveRestoreDialogPayload) {
	if (isOssArchiveEmbeddedFrame()) {
		postRestoreToParent(
			payload.error || {
				error_code: "OSS_ARCHIVED_NEED_RESTORE",
				data: {
					pid: payload.pid,
					order_id: payload.orderId,
					object_keys: payload.objectKeys,
					restore_status: payload.restoreStatus
				}
			}
		);
		return;
	}
	ossArchiveRestoreDialogPayload.value = payload;
	ossArchiveRestoreDialogVisible.value = true;
}

export function closeOssArchiveRestoreDialog() {
	ossArchiveRestoreDialogVisible.value = false;
	ossArchiveRestoreDialogPayload.value = null;
}

function payloadFromArchiveError(error: OssArchiveRejectError): OssArchiveRestoreDialogPayload {
	return {
		error,
		pid: error.data?.pid,
		orderId: error.data?.order_id,
		objectKeys: error.data?.object_keys,
		restoreStatus: error.data?.restore_status,
		uiCopy: getOssArchiveConfigSnapshot()?.uiCopy
	};
}

/**
 * 看图 catch 兜底：后端已返回归档码时，关闸/无订阅也不能把确认窗吞掉。
 * iframe 内只通知父页，主页面只弹一个确认窗。
 */
export function ensureOssArchiveRestoreDialog(error: unknown): boolean {
	if (ossArchiveRestoreDialogVisible.value) {
		return true;
	}
	const opened = dispatchOssArchiveError(error);
	if (opened && ossArchiveRestoreDialogVisible.value) {
		return true;
	}
	const normalized = opened || normalizeOssArchiveError(error);
	if (!normalized) {
		return Boolean(opened);
	}
	openOssArchiveRestoreDialog(payloadFromArchiveError(normalized));
	return true;
}

function isParentRestoreMessage(data: unknown): data is { error: OssArchiveRejectError } {
	if (!data || typeof data !== "object") {
		return false;
	}
	const msg = data as { type?: unknown; error?: unknown };
	return (
		msg.type === OSS_ARCHIVE_PARENT_MESSAGE_TYPE && !!msg.error && typeof msg.error === "object"
	);
}

export function initOssArchiveErrorHandler() {
	const stopSub = subscribeOssArchiveError((error) => {
		openOssArchiveRestoreDialog(payloadFromArchiveError(error));
	});
	const onMessage = (event: MessageEvent) => {
		if (!isParentRestoreMessage(event.data)) {
			return;
		}
		openOssArchiveRestoreDialog(payloadFromArchiveError(event.data.error));
	};
	window.addEventListener("message", onMessage);
	return () => {
		stopSub();
		window.removeEventListener("message", onMessage);
	};
}
